import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { Button, Row, Col, Table, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import Header from "../../../layout/Header/Header";
import { api, mensajeError, hoy, haceDias, dinero, numero, nombreUsuario, primero, Estado, pdfOrden } from "./gastosFlujoUtil";

// Paso 2 y 3 del flujo de gastos: la Orden se autoriza y se paga con uno o
// varios Gastos (cada pago sale del banco y da entrada a las existencias).
const conToken = () => ({ headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } });
const BANCOS_ESPECIALES = [process.env.REACT_APP_BANCO_BONIFICACION, process.env.REACT_APP_BANCO_DEVOLUCION];

function OrdenesGastosFlujo() {
  const { user } = useContext(AuthContext);
  const [inicio, setInicio] = useState(haceDias(90));
  const [fin, setFin] = useState(hoy());
  const [filtroStatus, setFiltroStatus] = useState("");
  const [ordenes, setOrdenes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [detalle, setDetalle] = useState(null); // { orden, renglones, gastos }
  const [pago, setPago] = useState(null); // { orden, renglones:[{...r, aPagar}], banco, fecha, descripcion }
  const [guardando, setGuardando] = useState(false);

  const cargar = () =>
    api.get(`ordenesGastosFlujoFechas/${inicio}/${fin}`).then(setOrdenes).catch((e) => Swal.fire({ icon: "error", title: "No se pudieron cargar", text: mensajeError(e) }));

  useEffect(() => {
    cargar();
  }, [inicio, fin]); // eslint-disable-line

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_URL_BANCOS, conToken())
      .then((r) => setBancos((r.data || []).filter((b) => b.is_active !== "No" && !BANCOS_ESPECIALES.includes(b._id))))
      .catch(() => {});
  }, []);

  // Solo las órdenes del flujo nuevo (ligadas a una requisición)
  const lista = useMemo(
    () => ordenes.filter((o) => o.requisicionesGastos && o.requisicionesGastos[0]).filter((o) => !filtroStatus || o.status === filtroStatus),
    [ordenes, filtroStatus]
  );

  async function abrir(o, accion) {
    try {
      const d = await api.get(`ordenesGastosFlujo/${o._id}`);
      if (accion === "pdf") return pdfOrden(d);
      if (accion === "pagar") {
        return setPago({
          orden: d.orden,
          renglones: d.renglones.filter((r) => r.pendiente > 0).map((r) => ({ ...r, aPagar: r.pendiente })),
          banco: "",
          fecha: hoy(),
          descripcion: "",
        });
      }
      setDetalle(d);
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo cargar", text: mensajeError(e) });
    }
  }

  async function autorizar(o, decision) {
    let motivo;
    if (decision === "Rechazada") {
      const res = await Swal.fire({ title: `Rechazar orden ${o.idOrdenGastos}`, input: "text", inputPlaceholder: "Motivo", showCancelButton: true, confirmButtonText: "Rechazar", cancelButtonText: "Cancelar" });
      if (res.dismiss || res.value === undefined) return;
      motivo = res.value;
    } else {
      const res = await Swal.fire({ icon: "question", title: `¿Autorizar la orden ${o.idOrdenGastos}?`, html: `${primero(o.proveedoresGastos, "nombre")} — <b>${dinero(o.total)}</b>`, showCancelButton: true, confirmButtonText: "Autorizar", cancelButtonText: "Cancelar" });
      if (!res.isConfirmed && !res.value) return;
    }
    try {
      await api.patch(`ordenesGastosFlujoAutorizar/${o._id}`, { decision, motivo });
      await cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo", text: mensajeError(e) });
    }
  }

  async function cancelar(o) {
    const res = await Swal.fire({ icon: "warning", title: `¿Cancelar la orden ${o.idOrdenGastos}?`, text: "La requisición vuelve a quedar autorizada para hacer otra orden.", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No" });
    if (!res.isConfirmed && !res.value) return;
    try {
      await api.patch(`ordenesGastosFlujoCancelar/${o._id}`);
      await cargar();
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se pudo cancelar", text: mensajeError(e) });
    }
  }

  const totalesPago = useMemo(() => {
    if (!pago) return { subtotal: 0, iva: 0, total: 0 };
    const subtotal = pago.renglones.reduce((s, r) => s + (Number(r.aPagar) || 0) * r.precio, 0);
    const iva = subtotal * ((pago.orden.impuestos || 0) / 100);
    return { subtotal, iva, total: subtotal + iva };
  }, [pago]);

  async function registrarPago() {
    if (!pago.banco) return Swal.fire({ icon: "warning", title: "Elige el banco" });
    const renglones = pago.renglones.filter((r) => Number(r.aPagar) > 0);
    if (!renglones.length) return Swal.fire({ icon: "warning", title: "Captura qué cantidades se pagan" });
    const excedido = renglones.find((r) => Number(r.aPagar) > r.pendiente);
    if (excedido) return Swal.fire({ icon: "warning", title: `${excedido.detalle}: solo quedan ${excedido.pendiente}` });
    const banco = bancos.find((b) => b._id === pago.banco);
    const ok = await Swal.fire({
      icon: "question",
      title: `Registrar pago de ${dinero(totalesPago.total)}`,
      html: `Sale de <b>${banco ? `${banco.banco} ${banco.cuenta || ""}` : ""}</b> y entra a existencias de insumos.`,
      showCancelButton: true,
      confirmButtonText: "Registrar",
      cancelButtonText: "Cancelar",
    });
    if (!ok.isConfirmed && !ok.value) return;
    setGuardando(true);
    try {
      const g = await api.post("gastosGeneralDeOrden", {
        ordenesGastos: pago.orden._id,
        bancos: pago.banco,
        fecha: pago.fecha,
        descripcion: pago.descripcion || undefined,
        renglones: renglones.map((r) => ({ articulosGastos: r._id, cantidad: Number(r.aPagar) })),
      });
      setPago(null);
      await cargar();
      Swal.fire({ icon: "success", title: `Gasto ${g.idGastoGeneral} registrado`, timer: 1500, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ icon: "error", title: "No se registró el pago", text: mensajeError(e) });
    }
    setGuardando(false);
  }

  if (!user.menu_gastos) return <Header />;

  return (
    <>
      <Header />
      <div className="card col-12" style={{ padding: "18px 22px" }}>
        <Row className="align-items-center">
          <Col md={7}>
            <h3 style={{ margin: 0 }}>Órdenes de gasto</h3>
            <small style={{ color: "#64748b" }}>Pasos 2 y 3: se autoriza la orden y se paga con uno o varios gastos.</small>
          </Col>
          <Col md={5} style={{ textAlign: "right" }}>
            <Button size="sm" color="primary" href="/RequisicionesGastos"><i className="fas fa-clipboard-list"></i> Requisiciones</Button>{" "}
            <Button size="sm" color="danger" href="/MenuGastos">Regresar</Button>
          </Col>
        </Row>
        <Row style={{ margin: "14px 0 8px" }}>
          <Col md={2}><Label>Desde</Label><Input bsSize="sm" type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></Col>
          <Col md={2}><Label>Hasta</Label><Input bsSize="sm" type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></Col>
          <Col md={2}>
            <Label>Estado</Label>
            <Input bsSize="sm" type="select" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
              <option value="">Todos</option>
              {["Por autorizar", "Autorizada", "Rechazada", "Pagada parcial", "Pagada", "Cancelada"].map((s) => <option key={s}>{s}</option>)}
            </Input>
          </Col>
        </Row>
        <Table size="sm" striped borderless className="table-responsive-xl">
          <thead>
            <tr><th>#</th><th>Fecha</th><th>Req.</th><th>Proveedor</th><th>Solicita</th><th style={{ textAlign: "right" }}>Total</th><th style={{ textAlign: "right" }}>Pagado</th><th style={{ textAlign: "right" }}>Saldo</th><th>Estado</th><th></th></tr>
          </thead>
          <tbody>
            {lista.map((o) => (
              <tr key={o._id}>
                <td>{o.idOrdenGastos}</td>
                <td>{o.fecha}</td>
                <td>{o.requisicionesGastos[0].idRequisicionGastos}</td>
                <td>{primero(o.proveedoresGastos, "nombre")}</td>
                <td>{primero(o.solicitantes)}</td>
                <td style={{ textAlign: "right" }}>{dinero(o.total)}</td>
                <td style={{ textAlign: "right" }}>{dinero(o.pagado)}</td>
                <td style={{ textAlign: "right", fontWeight: 700 }}>{dinero(o.saldo)}</td>
                <td><Estado valor={o.status} />{o.status === "Rechazada" && o.motivoRechazo ? <div style={{ fontSize: "0.7rem", color: "#991b1b" }}>{o.motivoRechazo}</div> : null}</td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  <Button size="sm" color="info" title="Ver" onClick={() => abrir(o)}><i className="fas fa-eye"></i></Button>{" "}
                  <Button size="sm" color="danger" title="PDF" onClick={() => abrir(o, "pdf")}><i className="far fa-file-pdf"></i></Button>{" "}
                  {o.status === "Por autorizar" && user.gastos_autorizar ? (
                    <>
                      <Button size="sm" color="success" title="Autorizar" onClick={() => autorizar(o, "Autorizada")}><i className="fas fa-check"></i></Button>{" "}
                      <Button size="sm" color="warning" title="Rechazar" onClick={() => autorizar(o, "Rechazada")}><i className="fas fa-times"></i></Button>{" "}
                    </>
                  ) : null}
                  {["Autorizada", "Pagada parcial"].includes(o.status) && user.gastos_create ? (
                    <><Button size="sm" color="primary" onClick={() => abrir(o, "pagar")}><i className="fas fa-money-bill-wave"></i> Registrar pago</Button>{" "}</>
                  ) : null}
                  {["Por autorizar", "Autorizada", "Rechazada"].includes(o.status) && !(o.pagado > 0) && user.gastos_create ? (
                    <Button size="sm" color="secondary" title="Cancelar" onClick={() => cancelar(o)}><i className="fas fa-ban"></i></Button>
                  ) : null}
                </td>
              </tr>
            ))}
            {!lista.length ? <tr><td colSpan={10} style={{ color: "#94a3b8", textAlign: "center" }}>Sin órdenes en el periodo. Las órdenes se hacen desde una requisición autorizada.</td></tr> : null}
          </tbody>
        </Table>
      </div>

      {/* Detalle */}
      <Modal size="lg" isOpen={!!detalle} toggle={() => setDetalle(null)}>
        <ModalHeader toggle={() => setDetalle(null)}>
          Orden de gasto {detalle ? detalle.orden.idOrdenGastos : ""} {detalle ? <Estado valor={detalle.orden.status} /> : null}
        </ModalHeader>
        <ModalBody>
          {detalle ? (
            <>
              <p style={{ fontSize: "0.85rem" }}>
                {detalle.orden.fecha} · <b>{primero(detalle.orden.proveedoresGastos, "nombre")}</b> · Requisición {detalle.orden.requisicionesGastos[0].idRequisicionGastos} · Solicita {primero(detalle.orden.solicitantes)}
                {detalle.orden.autorizo && detalle.orden.autorizo[0] ? ` · ${detalle.orden.status === "Rechazada" ? "Rechazó" : "Autorizó"} ${nombreUsuario(detalle.orden.autorizo)}` : ""}
              </p>
              <Table size="sm" bordered>
                <thead><tr><th>Insumo</th><th style={{ textAlign: "right" }}>Cantidad</th><th style={{ textAlign: "right" }}>Pagado</th><th style={{ textAlign: "right" }}>Pendiente</th><th style={{ textAlign: "right" }}>Precio</th><th style={{ textAlign: "right" }}>Importe</th></tr></thead>
                <tbody>
                  {detalle.renglones.map((r) => (
                    <tr key={r._id}>
                      <td>{r.detalle}</td>
                      <td style={{ textAlign: "right" }}>{numero(r.cantidad)}</td>
                      <td style={{ textAlign: "right" }}>{numero(r.recibido)}</td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>{numero(r.pendiente)}</td>
                      <td style={{ textAlign: "right" }}>{dinero(r.precio)}</td>
                      <td style={{ textAlign: "right" }}>{dinero(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={5} style={{ textAlign: "right" }}>Subtotal / IVA {detalle.orden.impuestos || 0}%</td><td style={{ textAlign: "right" }}>{dinero(detalle.orden.subtotal)} / {dinero(detalle.orden.iva)}</td></tr>
                  <tr><td colSpan={5} style={{ textAlign: "right", fontWeight: 700 }}>Total · Pagado · Saldo</td><td style={{ textAlign: "right", fontWeight: 700 }}>{dinero(detalle.orden.total)} · {dinero(detalle.orden.pagado)} · {dinero(detalle.orden.saldo)}</td></tr>
                </tfoot>
              </Table>
              <h6>Pagos (gastos)</h6>
              <Table size="sm" bordered>
                <thead><tr><th>Gasto</th><th>Fecha</th><th>Banco</th><th style={{ textAlign: "right" }}>Total</th><th>Estado</th></tr></thead>
                <tbody>
                  {detalle.gastos.map((g) => (
                    <tr key={g._id} style={{ opacity: g.is_active === "No" ? 0.5 : 1 }}>
                      <td>{g.idGastoGeneral}</td>
                      <td>{g.fecha}</td>
                      <td>{g.bancos && g.bancos[0] ? `${g.bancos[0].banco} ${g.bancos[0].cuenta || ""}` : ""}</td>
                      <td style={{ textAlign: "right" }}>{dinero(g.total)}</td>
                      <td>{g.is_active === "No" ? "Cancelado" : "Activo"}</td>
                    </tr>
                  ))}
                  {!detalle.gastos.length ? <tr><td colSpan={5} style={{ color: "#94a3b8", textAlign: "center" }}>Sin pagos todavía.</td></tr> : null}
                </tbody>
              </Table>
              <small style={{ color: "#64748b" }}>Para cancelar un pago, cancélalo en el listado de Gastos: la orden y las existencias se ajustan solas.</small>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter><Button color="secondary" onClick={() => setDetalle(null)}>Cerrar</Button></ModalFooter>
      </Modal>

      {/* Registrar pago */}
      <Modal size="lg" isOpen={!!pago} toggle={() => setPago(null)}>
        <ModalHeader toggle={() => setPago(null)}>Registrar pago — orden {pago ? pago.orden.idOrdenGastos : ""}</ModalHeader>
        <ModalBody>
          {pago ? (
            <>
              <Row>
                <Col md={5}>
                  <Label>Banco (de donde sale) *</Label>
                  <Input type="select" value={pago.banco} onChange={(e) => setPago({ ...pago, banco: e.target.value })}>
                    <option value="">Selecciona</option>
                    {bancos.map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta} ({b.moneda})</option>)}
                  </Input>
                </Col>
                <Col md={3}><Label>Fecha</Label><Input type="date" value={pago.fecha} onChange={(e) => setPago({ ...pago, fecha: e.target.value })} /></Col>
                <Col md={4}><Label>Descripción</Label><Input value={pago.descripcion} placeholder={`Orden de gasto ${pago.orden.idOrdenGastos}`} onChange={(e) => setPago({ ...pago, descripcion: e.target.value })} /></Col>
              </Row>
              <Table size="sm" bordered style={{ marginTop: 12 }}>
                <thead><tr><th>Insumo</th><th style={{ textAlign: "right" }}>Pendiente</th><th style={{ width: 130 }}>A pagar</th><th style={{ textAlign: "right" }}>Precio</th><th style={{ textAlign: "right" }}>Importe</th></tr></thead>
                <tbody>
                  {pago.renglones.map((r) => (
                    <tr key={r._id}>
                      <td>{r.detalle}</td>
                      <td style={{ textAlign: "right" }}>{numero(r.pendiente)}</td>
                      <td>
                        <Input bsSize="sm" type="number" min="0" max={r.pendiente} step="any" value={r.aPagar}
                          onChange={(e) => { const v = e.target.value; setPago((p) => ({ ...p, renglones: p.renglones.map((x) => (x._id === r._id ? { ...x, aPagar: v } : x)) })); }} />
                      </td>
                      <td style={{ textAlign: "right" }}>{dinero(r.precio)}</td>
                      <td style={{ textAlign: "right" }}>{dinero((Number(r.aPagar) || 0) * r.precio)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr><td colSpan={4} style={{ textAlign: "right" }}>Subtotal</td><td style={{ textAlign: "right" }}>{dinero(totalesPago.subtotal)}</td></tr>
                  <tr><td colSpan={4} style={{ textAlign: "right" }}>IVA {pago.orden.impuestos || 0}%</td><td style={{ textAlign: "right" }}>{dinero(totalesPago.iva)}</td></tr>
                  <tr><td colSpan={4} style={{ textAlign: "right", fontWeight: 700 }}>Total del pago</td><td style={{ textAlign: "right", fontWeight: 700 }}>{dinero(totalesPago.total)}</td></tr>
                </tfoot>
              </Table>
              <small style={{ color: "#64748b" }}>Para un pago parcial baja las cantidades; lo que falte queda pendiente en la orden.</small>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={registrarPago} disabled={guardando}>{guardando ? "Registrando..." : "Registrar pago"}</Button>
          <Button color="secondary" onClick={() => setPago(null)}>Cerrar</Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default OrdenesGastosFlujo;
